import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '@/services/apiClient';
import { TemplateThumbnail } from '@/components/marketing/TemplateThumbnail';
import './ftp-editor.css';

export interface FtpTemplateRow {
  id: string;
  name: string;
  /**
   * Empty until hydrated. The list endpoint returns metadata only, so this fills
   * in when the template is selected (see handleSelectTemplate); card thumbnails
   * come from the preview endpoint instead.
   */
  html_content: string;
  /** From the list row: whether markup exists to fetch at all. */
  has_content?: boolean;
  plain_content?: string;
  json_content?: string;
  folder?: string;
  template_type?: string;
  updated_at?: string;
  subject?: string;
  preheader?: string;
  from_name?: string;
  from_email?: string;
}

export interface TemplateMetadata {
  subject?: string;
  preheader?: string;
  fromName?: string;
  fromEmail?: string;
}

export interface FtpTemplatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (htmlContent: string, plainContent?: string, jsonContent?: string, metadata?: TemplateMetadata) => void;
  mode?: 'apply' | 'select';
  onSelect?: (template: unknown) => void;
  /**
   * Hides templates the caller can't take (the DnD editor can't load plain-HTML
   * ones). Applied to the resolved list, so rows whose content arrives from the
   * follow-up /full fetch are judged on that content, not on the empty list row.
   * Pass a stable reference — an inline arrow re-filters on every render.
   */
  filter?: (template: FtpTemplateRow) => boolean;
  /** Empty-state text when `filter` leaves nothing to show. */
  emptyNote?: string;
}

export function FtpTemplatePicker({
  isOpen,
  onClose,
  onApply,
  mode = 'apply',
  onSelect,
  filter,
  emptyNote = 'No templates found',
}: FtpTemplatePickerProps) {
  const [templates, setTemplates] = useState<FtpTemplateRow[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<FtpTemplateRow | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [folders, setFolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingFull, setLoadingFull] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => window.clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedTemplate(null);
      setSearchQuery('');
      setDebouncedSearch('');
      setSelectedFolder('');
      setTemplates([]);
      setFolders([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params: Record<string, string> = { active: '1' };
        if (debouncedSearch) params.search = debouncedSearch;
        if (selectedFolder) params.folder = selectedFolder;
        params.perPage = '100';
        // Metadata only. 100 inlined email bodies can exceed the response limit,
        // which fails the request and leaves the picker empty.
        params.withContent = '0';

        const [templatesRes, foldersRes] = await Promise.all([
          apiClient.get('/marketing/email-templates', { params }),
          templates.length === 0 && !debouncedSearch && !selectedFolder
            ? apiClient.get('/marketing/email-templates/folders').catch(() => ({ data: { data: [] } }))
            : Promise.resolve(null),
        ]);
        if (cancelled) return;

        const rawList: unknown[] = templatesRes.data?.data ?? [];
        const list: FtpTemplateRow[] = rawList.map((t: unknown) => {
          const row = t as Record<string, unknown>;
          return {
            id: String(row.emailTemplateId ?? row.id ?? ''),
            name: String(row.name ?? 'Untitled'),
            html_content: String(row.htmlContent ?? row.html_content ?? ''),
            has_content: row.hasContent === true || Number(row.htmlBytes ?? 0) > 0,
            plain_content: row.plainContent != null ? String(row.plainContent) : undefined,
            json_content: row.jsonContent != null ? String(row.jsonContent) : undefined,
            folder: row.folder != null ? String(row.folder) : undefined,
            template_type: row.templateType != null ? String(row.templateType) : undefined,
            updated_at: row.createdAt != null ? String(row.createdAt) : undefined,
            subject: row.subject != null ? String(row.subject) : undefined,
            preheader: row.preheader != null ? String(row.preheader) : undefined,
            from_name: row.fromName != null ? String(row.fromName) : undefined,
            from_email: row.fromEmail != null ? String(row.fromEmail) : undefined,
          };
        });
        setTemplates(list);

        if (foldersRes) {
          const folderList: { name?: string }[] = foldersRes.data?.data ?? [];
          setFolders(
            folderList
              .map((f) => f.name ?? '')
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b)),
          );
        }

        // Deliberately no eager content backfill here. This used to fetch /full for
        // every row the list came back without markup for — one Promise.allSettled
        // over the whole page. With the list now metadata-only that would be up to
        // 100 parallel requests, each also carrying the DnD model: strictly worse
        // than the oversized response it replaced. Thumbnails come from the preview
        // endpoint per visible card, and handleSelectTemplate hydrates the one
        // template the viewer actually picks.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, debouncedSearch, selectedFolder]);

  const handleSelectTemplate = async (row: FtpTemplateRow) => {
    setSelectedTemplate(row);
    // Always resolve /full when json_content is unknown so the caller can tell a
    // DnD template from an HTML one (the list endpoint omits json_content).
    if (row.html_content && row.plain_content !== undefined && row.json_content !== undefined) return;
    setLoadingFull(true);
    try {
      const res = await apiClient.get(`/marketing/email-templates/${row.id}/full`);
      const full = res.data?.data ?? res.data;
      setSelectedTemplate((prev) =>
        prev?.id === row.id
          ? {
              ...prev,
              html_content: String(full.htmlContent ?? full.html_content ?? ''),
              plain_content: full.plainContent != null ? String(full.plainContent) : undefined,
              json_content: full.jsonContent != null ? String(full.jsonContent) : undefined,
            }
          : prev,
      );
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === row.id
            ? {
                ...t,
                html_content: String(full.htmlContent ?? full.html_content ?? ''),
                plain_content: full.plainContent != null ? String(full.plainContent) : undefined,
                json_content: full.jsonContent != null ? String(full.jsonContent) : undefined,
              }
            : t,
        ),
      );
    } catch {
      // silently handle — preview will stay empty
    } finally {
      setLoadingFull(false);
    }
  };

  const displayFolders = useMemo(() => folders, [folders]);

  const visibleTemplates = useMemo(
    () => (filter ? templates.filter(filter) : templates),
    [templates, filter],
  );

  if (!isOpen) return null;

  const fmtDate = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  };

  const handlePrimary = () => {
    if (!selectedTemplate?.html_content) return;
    if (!window.confirm('This will replace your current content. Continue?')) return;
    if (mode === 'select' && onSelect) onSelect(selectedTemplate);
    else onApply(selectedTemplate.html_content, selectedTemplate.plain_content, selectedTemplate.json_content, {
      subject: selectedTemplate.subject,
      preheader: selectedTemplate.preheader,
      fromName: selectedTemplate.from_name,
      fromEmail: selectedTemplate.from_email,
    });
    onClose();
  };

  return createPortal(
    <div
      className="ftp-modal-overlay active"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ftp-modal ftp-template-picker"
        role="dialog"
        aria-labelledby="ftp-template-picker-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="ftp-modal-header">
          <h3 id="ftp-template-picker-title">Choose Template</h3>
          <button type="button" className="ftp-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="ftp-modal-body ftp-tmpl-body">
          <div className="ftp-tmpl-search-bar">
            <div className="ftp-tmpl-folder-wrap">
              <select
                className="ftp-tmpl-folder-select"
                value={selectedFolder}
                onChange={(e) => setSelectedFolder(e.target.value)}
                aria-label="Folder"
              >
                <option value="">All folders</option>
                {displayFolders.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>
            <div className="ftp-tmpl-search-wrap">
              <i aria-hidden />
              <input
                type="search"
                placeholder="Search templates…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          <div className="ftp-tmpl-layout">
            <div className="ftp-tmpl-list">
              {loading ? (
                <div className="ftp-picker-loading">
                  <div className="ftp-spinner" />
                </div>
              ) : visibleTemplates.length === 0 ? (
                <div className="ftp-tmpl-preview-empty">
                  <p>{emptyNote}</p>
                </div>
              ) : (
                visibleTemplates.map((t, i) => (
                  <div
                    key={t.id ? t.id : `tmpl-${i}`}
                    role="button"
                    tabIndex={0}
                    className={`ftp-tmpl-card${selectedTemplate?.id === t.id ? ' selected' : ''}`}
                    onClick={() => handleSelectTemplate(t)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectTemplate(t);
                      }
                    }}
                  >
                    <div className="ftp-tmpl-card-thumb">
                      <TemplateThumbnail
                        templateId={Number(t.id)}
                        hasContent={t.has_content !== false}
                        title={t.name}
                        html={t.html_content || undefined}
                      />
                    </div>
                    <div className="ftp-tmpl-card-info">
                      <div className="ftp-tmpl-card-name">{t.name}</div>
                      <div className="ftp-tmpl-card-meta">
                        {t.template_type ? <span className="ftp-tmpl-card-type">{t.template_type}</span> : null}
                        {t.updated_at ? <span className="ftp-tmpl-card-date">{fmtDate(t.updated_at)}</span> : null}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="ftp-tmpl-preview-pane">
              {loadingFull ? (
                <div className="ftp-picker-loading">
                  <div className="ftp-spinner" />
                </div>
              ) : !selectedTemplate?.html_content ? (
                <div className="ftp-tmpl-preview-empty">
                  <i aria-hidden />
                  <p>Select a template to preview</p>
                </div>
              ) : (
                <iframe
                  className="ftp-tmpl-preview-frame"
                  title="Template preview"
                  srcDoc={selectedTemplate.html_content}
                  style={{ display: 'block' }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="ftp-modal-footer">
          <span className="ftp-picker-info">Applying loads the template HTML into the editor.</span>
          <div className="ftp-modal-actions">
            <button type="button" className="ftp-btn ftp-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="ftp-btn ftp-btn-primary"
              onClick={handlePrimary}
              disabled={!selectedTemplate?.html_content || loadingFull}
            >
              {mode === 'select' ? 'Select Template' : 'Apply Template'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
